import { useState } from "react";
import { Building2, Check, ChevronDown, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";

const ROLE_LABELS = { owner: "Titolare", admin: "Admin", operator: "Operatore", viewer: "Lettura" };

export default function CompanySwitcher() {
  const { companies, company, companyId, switchCompany, refresh } = useCompany();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [sector, setSector] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/companies", { name, sector, jurisdiction });
      await refresh();
      switchCompany(data.company_id);
      setCreating(false);
      setOpen(false);
      setName(""); setSector(""); setJurisdiction("");
      toast.success(`Workspace pulito creato: ${data.name}. DAD parte da zero — nessun dato inventato.`);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative" data-testid="company-switcher">
      <button data-testid="company-switcher-btn" aria-expanded={open} aria-label="Seleziona azienda" onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-full px-3 py-1.5 text-xs border border-cyan-400/20 bg-cyan-400/5 hover:bg-cyan-400/10 transition-colors max-w-[180px] sm:max-w-[260px]">
        <Building2 size={14} className="text-cyan-300 shrink-0" />
        <span className="truncate text-gray-200">{company?.name || "Seleziona azienda"}</span>
        <ChevronDown size={12} className="text-gray-500 shrink-0" />
      </button>

      {open && (
        <div className="absolute left-0 top-10 z-50 w-72 glass-strong rounded-2xl p-2 shadow-2xl" data-testid="company-switcher-menu">
          <div className="text-[10px] tracking-[0.2em] text-gray-500 uppercase px-3 py-2">Le tue aziende</div>
          {companies.map((c) => (
            <button key={c.company_id} data-testid={`company-option-${c.company_id}`}
              onClick={() => { switchCompany(c.company_id); setOpen(false); }}
              className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-white/[0.04] text-left">
              <span className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${c.company_id === companyId ? "bg-cyan-400 text-[#030712]" : "border border-gray-700"}`}>
                {c.company_id === companyId && <Check size={10} />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm text-gray-200 truncate">{c.name}</span>
                <span className="block text-[10px] text-gray-500">
                  {ROLE_LABELS[c.role] || c.role}{c.demo ? " · DEMO" : ""}
                </span>
              </span>
            </button>
          ))}
          <div className="border-t border-white/5 mt-1 pt-1">
            <button data-testid="company-create-open" onClick={() => setCreating(true)}
              className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-cyan-400/10 text-left text-cyan-300">
              <Plus size={15} /> <span className="text-sm">Nuova azienda (workspace pulito)</span>
            </button>
          </div>
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
          role="dialog" aria-modal="true" aria-label="Crea azienda" data-testid="company-create-modal" onClick={() => setCreating(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={create}
            className="glass-strong rounded-3xl p-7 w-full max-w-md space-y-4 fade-up">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-lg">Nuova azienda</h2>
              <button type="button" aria-label="Chiudi" onClick={() => setCreating(false)} className="text-gray-500 hover:text-gray-300"><X size={18} /></button>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">
              Il workspace parte da zero: nessun documento, dato finanziario o regola preesistente.
              DAD costruisce la conoscenza solo dalle informazioni che fornisci tu.
            </p>
            <div>
              <label className="text-xs text-gray-500 mb-1.5 block">Nome azienda *</label>
              <input data-testid="company-create-name" required value={name} onChange={(e) => setName(e.target.value)}
                className="input-dark" placeholder="Es. Test DAD SAGL" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1.5 block">Settore</label>
              <input data-testid="company-create-sector" value={sector} onChange={(e) => setSector(e.target.value)}
                className="input-dark" placeholder="Es. Ristorazione" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1.5 block">Giurisdizione</label>
              <input data-testid="company-create-jurisdiction" value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)}
                className="input-dark" placeholder="Es. Svizzera · Cantone Ticino" />
            </div>
            {err && <div data-testid="company-create-error" className="text-sm text-red-400">{err}</div>}
            <button data-testid="company-create-submit" type="submit" disabled={busy} className="btn-primary w-full">
              {busy ? "Creazione…" : "Crea workspace pulito"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
